/* eslint-disable react-refresh/only-export-components */
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { APPROVED, DOWNSALE } from 'Router/routes';
import { useFirstLoanOfferQuery } from 'hooks/webapi/application/useFirstLoanOfferQuery';
import { useApplicationQuery, useClientOfferQuery } from 'hooks/webapi';
import { useConstraints } from 'hooks/context';
import getValuetFromQueryString from 'hooks/calculator/getValueFromQueryString';
import useAffiliate from 'hooks/webapi/useAffiliate';
import { useSession } from 'session';

type CalculatorContextType = ReturnType<typeof useCalculatorValue>;

export const CalculatorContext = createContext({} as CalculatorContextType);

function useCalculatorValue() {
  const { constraints } = useConstraints();
  const tokenExists = useSession((state) => state.status === 'authenticated');

  const { affiliateForm } = useAffiliate();

  // react-router
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  // Local state
  const [amount, setAmount] = useState<number>();
  const beforeOpenedAmount = useRef<number | null | undefined>(null);

  const amountParam = searchParams.get('amount');

  const { data: application } = useApplicationQuery(tokenExists);
  const {
    data: clientOffer,
    isLoading: clientOfferIsLoading,
    isError: isClientError,
  } = useClientOfferQuery(
    {
      amount,
      term: constraints?.termInterval.defaultValue,
    },
    tokenExists &&
      constraints !== undefined &&
      ![APPROVED, DOWNSALE].includes(pathname),
  );
  const { data: firstLoanOffer, isLoading: firstLoanOfferIsLoading } =
    useFirstLoanOfferQuery(
      {
        amount,
        term: constraints?.termInterval.defaultValue,
      },
      !tokenExists &&
        amount !== undefined &&
        constraints !== undefined &&
        application === undefined,
    );

  const loading = clientOfferIsLoading || firstLoanOfferIsLoading;

  // Init logic
  const resolvedAmount = useMemo(() => {
    if (!constraints) return null;

    if (affiliateForm?.amount) return affiliateForm.amount;

    const queryAmount = getValuetFromQueryString(
      amountParam,
      constraints.amountInterval,
    );

    return queryAmount;
  }, [tokenExists, application, constraints, amountParam, affiliateForm]);

  useEffect(() => {
    // Initialize amount value
    if (resolvedAmount && !amount) {
      setAmount(resolvedAmount as number);
    }
  }, [resolvedAmount, amount]);

  // Force to update amount and term
  useEffect(() => {
    if (application?.amount) setAmount(application?.amount);
  }, [application?.amount]);

  const setAmountToSessionStorage = useCallback((newAmount: number) => {
    // SessionStorage accepts only string values
    sessionStorage.setItem('calculatorAmount', String(newAmount));
    setAmountToSessionStorage(newAmount);
  }, []);

  // Handlers
  const handleAmountChange = useCallback((newAmount: number) => {
    setAmount(newAmount);
  }, []);

  const backupCurrentAmount = () => {
    beforeOpenedAmount.current = amount;
  };

  const recoverPreviousAmount = () => {
    if (beforeOpenedAmount.current && amount !== beforeOpenedAmount.current) {
      setAmount(beforeOpenedAmount.current);
    }
  };

  const loadAmountFromSessionStorage = useCallback(() => {
    setAmount(Number(sessionStorage.getItem('calculatorAmount')));
  }, []);

  return {
    amount,
    backupCurrentAmount,
    recoverPreviousAmount,
    handleAmountChange,
    loadAmountFromSessionStorage,
    offer: clientOffer ?? firstLoanOffer,
    loading,
    isClientError,
  };
}

export function CalculatorProvider({ children }: { children: ReactNode }) {
  const value = useCalculatorValue();

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
}
